import React, { useEffect, useState } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { toast } from '../../../../../../ui/toast';
import { FloatingLabelSelect } from '../../../../../../ui';
import {
  IInterviewRound,
  getInterviewRound,
  updateInterviewRound,
} from '../../../../../../utils/api_service/SmartInterview';
import { renderTipTapJsonToHtml } from '../Configurations/Hooks/renderTipTap';
import RichTextEditor from './Components';
import email_template from './email_template.json';

type TemplateKey = keyof typeof email_template;

export const InviteTemplate: React.FC<{ interviewId: string }> = ({ interviewId }) => {
  const [mode, setMode] = useState<TemplateKey>('scheduling');
  const [loading, setLoading] = useState(true);

  // each template’s JSON
  const [docs, setDocs] = useState<Record<TemplateKey, string>>({
    scheduling: JSON.stringify(email_template.scheduling.defaultJson),
    meetingDetails: JSON.stringify(email_template.meetingDetails.defaultJson),
  });

  // load the saved JSON on mount
  useEffect(() => {
    (async () => {
      try {
        const res = await getInterviewRound(interviewId);
        const data = (await res.json()) as any;

        // Helper to safely parse JSON or return default
        const safeParseJson = (
          jsonString: string | null | undefined,
          defaultJson: any,
        ) => {
          if (!jsonString) return JSON.stringify(defaultJson);
          try {
            return JSON.stringify(JSON.parse(jsonString));
          } catch {
            return JSON.stringify(defaultJson);
          }
        };

        setDocs({
          scheduling: safeParseJson(
            data.schedulingTemplateJson,
            email_template.scheduling.defaultJson,
          ),
          meetingDetails: safeParseJson(
            data.meetingDetailsTemplateJson,
            email_template.meetingDetails.defaultJson,
          ),
        });
      } catch {
        toast.error('Failed to load templates', {
          position: 'bottom-center',
        });
      } finally {
        setLoading(false);
      }
    })();
  }, [interviewId]);

  if (loading) {
    return (
      <Box sx={{ p: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
        <CircularProgress size={20} />
        <Typography variant="body2" color="text.secondary">
          Loading templates…
        </Typography>
      </Box>
    );
  }

  const tpl = email_template[mode];
  const jsonString = docs[mode];

  const handleSave = async (_html: string, json: string) => {
    try {
      const parsedJson = JSON.parse(json);
      const html = renderTipTapJsonToHtml(parsedJson);
      const isScheduling = mode === 'scheduling';

      const res = await getInterviewRound(interviewId);
      if (!res.ok) {
        throw new Error('Failed to fetch interview round data');
      }
      const raw = await res.json();

      if (!raw.title || !raw.jobOpeningId || !raw.host?.id) {
        throw new Error('Missing required fields in interview round data');
      }

      const payload: IInterviewRound = {
        title: raw.title,
        jobOpeningId: raw.jobOpeningId,
        host: raw.host.id,
        panelists: (raw.panelists || []).map((p: any) => p.id),
        timeSlots: raw.timeSlots || [],
        schedulingTemplateHtml: isScheduling
          ? html
          : raw.schedulingTemplateHtml || '',
        schedulingTemplateJson: isScheduling
          ? json
          : raw.schedulingTemplateJson || '',
        meetingDetailsTemplateHtml: !isScheduling
          ? html
          : raw.meetingDetailsTemplateHtml || '',
        meetingDetailsTemplateJson: !isScheduling
          ? json
          : raw.meetingDetailsTemplateJson || '',
        meetingDuration: raw.meetingDuration || 30,
      };
      const upd = await updateInterviewRound(interviewId, payload);
      const responseData = await upd.json();

      if (!upd.ok) {
        throw new Error(responseData.error || 'Update failed');
      }

      setDocs((d) => ({
        ...d,
        [mode]: json,
      }));

      toast.success(`${tpl.title} updated!`, {
        position: 'bottom-center',
      });
    } catch (e) {
      toast.error((e as Error).message || 'Failed to save template', {
        position: 'bottom-center',
      });
    }
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 2 } }}>
      <Box sx={{ maxWidth: { xs: '100%', sm: 280 }, pb: 2 }}>
        <FloatingLabelSelect
          value={{ label: tpl.title, value: mode }}
          options={Object.entries(email_template).map(([key, val]) => ({
            label: val.title,
            value: key as TemplateKey,
          }))}
          onChange={(opt) => setMode(opt.value as TemplateKey)}
          placeholder="Select Email Template"
        />
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        {tpl.description}
      </Typography>

      <RichTextEditor
        key={mode}
        jsonStringTemplate={jsonString}
        validVariables={tpl.variables}
        saveTemplate={handleSave}
        buttonText="Save This Email"
      />
    </Box>
  );
};

export default InviteTemplate;
